import { useRef, type ReactNode } from "react";
import { AuthProvider as TanstackAuthProvider } from "@tellescope/tanstack-query";
import { getApiURL } from "../config/api";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelectedHost } from "@tellescope/ui";
import { PUBLIC_PATHS } from "../routing/get-public-paths";

export interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();
  const [selectedHost] = useSelectedHost();
  // Tracks whether we're in a pending MFA flow. Set synchronously in the login
  // mutation's onSuccess callback (before invalidateAuthQueries fires), and cleared
  // when MFA completes or is cancelled via resetAuth/logout.
  // Returning true from handleUnauthenticated suppresses the SDK's clearState() call,
  // which would otherwise wipe the session and bounce the user back to login.
  const requiresMFARef = useRef(false);
  const { pathname } = useLocation();
  const pathnameRef = useRef(pathname);
  pathnameRef.current = pathname;

  return (
    <TanstackAuthProvider
      options={{
        sessionOptions: {
          host: getApiURL(selectedHost),
          handleUnauthenticated: async () => {
            if (requiresMFARef.current) return true;
            if (!PUBLIC_PATHS.includes(pathnameRef.current)) {
              navigate("/login");
            }
          },
        },
        loginMutationOptions: {
          onSuccess: (result) => {
            requiresMFARef.current = result.requiresMFA ?? false;
          },
        },
        loginWithTokenOptions: {
          onSuccess: (user) => {
            requiresMFARef.current = user.requiresMFA ?? false;
          },
        },
        logoutMutationOptions: {
          onSuccess: () => {
            requiresMFARef.current = false;
          },
        },
      }}
    >
      {children}
    </TanstackAuthProvider>
  );
};
